/**
 * Keeps the floating basket's count honest.
 *
 * Liquid renders the correct number on load. Two things can make it stale
 * afterwards, and both are handled here:
 *
 *   1. Adds made through fetch — the hotel cross-sell on ticket pages and the
 *      cart upsells — never reload the page.
 *   2. Back/forward navigation restores a cached page whose badge was rendered
 *      before an add.
 *
 * Everything degrades quietly: if /cart.js is unreachable the badge simply keeps
 * whatever Liquid rendered rather than showing a wrong number or breaking.
 */

(function () {
  "use strict";

  /* ------------------------------------------------------------------ */
  /* Cart back link                                                      */
  /* ------------------------------------------------------------------ */

  /*
   * Points the cart's back link at the page the customer actually came from.
   *
   * Liquid renders a safe fallback href, and this only replaces it when the
   * referrer is somewhere sensible to return to. Deliberately excluded:
   *
   *   - other origins, which would send them off the store entirely
   *   - the cart itself, which would be a link to the current page
   *   - checkout, since going back into a checkout flow from the cart is not
   *     what "back" should mean here
   *
   * Using a real href rather than history.back() keeps the link
   * right-clickable, survives JavaScript being off, and cannot re-trigger the
   * POST that /cart/add redirected from.
   */
  (function backLink() {
    var link = document.querySelector("[data-cart-back]");
    if (!link || !document.referrer) return;

    var ref;
    try {
      ref = new URL(document.referrer);
    } catch (e) {
      return;
    }

    if (ref.origin !== window.location.origin) return;
    if (/^\/(cart|checkout)/.test(ref.pathname)) return;

    link.href = ref.pathname + ref.search;

    var label = link.querySelector("[data-cart-back-label]");
    if (label) label.textContent = "Back";
  })();

  /* ------------------------------------------------------------------ */
  /* Basket count                                                        */
  /* ------------------------------------------------------------------ */

  var fab = document.querySelector("[data-cart-fab]");
  if (!fab) return;

  var badge = fab.querySelector("[data-cart-count]");

  function paint(count) {
    if (typeof count !== "number" || count < 0) return;
    fab.dataset.count = String(count);
    if (badge) badge.textContent = String(count);
    fab.setAttribute(
      "aria-label",
      "View cart, " + count + (count === 1 ? " item" : " items")
    );
  }

  function refresh() {
    fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (c) paint(c.item_count); })
      .catch(function () { /* keep the server-rendered figure */ });
  }

  /* Anything adding to the cart in the background fires this. */
  document.addEventListener("cart:updated", refresh);

  /* pageshow with persisted=true means the page came from the back/forward
     cache, where the rendered badge may predate an add. */
  window.addEventListener("pageshow", function (e) {
    if (e.persisted) refresh();
  });
})();

/**
 * Cart upsell add buttons: acknowledge the tap, and only add once.
 *
 * The forms are plain POSTs to /cart/add, so between the tap and the reload
 * there is a stretch of nothing — no spinner, no state change, on a card that
 * looks exactly as it did. That silence is what makes people tap again, and a
 * second tap is a second parking pass they did not want and will ask for a
 * refund on.
 *
 * The plus turns into a spinner and the label is left alone. Swapping the text
 * to Adding… was the first attempt and it was wrong: the longer word grew the
 * button from 81px to 96px, which moved it out from under the thumb that had
 * just pressed it — the exact reflow this is meant to prevent. Pinning width
 * (not min-width, which only stops shrinking) holds it still.
 *
 * Deliberately not `button.disabled`: a disabled control can be dropped from
 * the submission in some browsers, and it also drops out of the tab order
 * mid-interaction. aria-disabled plus pointer-events (see theme.css) blocks
 * the second press without touching the submission already in flight, and
 * aria-busy is what actually tells a screen reader something is happening.
 *
 * Progressive enhancement only. With script off the button still posts and
 * still works — it just does it quietly, as it did before.
 */
(function () {
  "use strict";

  function reset(btn) {
    btn.removeAttribute("aria-disabled");
    btn.removeAttribute("aria-busy");
    btn.classList.remove("is-adding");
    btn.style.width = "";
  }

  document.addEventListener(
    "submit",
    function (e) {
      var form = e.target;
      if (!form || !form.classList || !form.classList.contains("cart-upsell__form")) return;

      var btn = form.querySelector(".cart-upsell__add");
      if (!btn || btn.getAttribute("aria-disabled") === "true") return;

      /* Freeze the width before the plus becomes a ring, so nothing shifts
         under the thumb that just pressed it. */
      btn.style.width = btn.getBoundingClientRect().width + "px";
      btn.setAttribute("aria-disabled", "true");
      btn.setAttribute("aria-busy", "true");
      btn.classList.add("is-adding");
    },
    /* Capture, so this runs before anything that might stop propagation. */
    true
  );

  /* Restoring from the back/forward cache: without this the page comes back
     with a button that spins forever and can never be pressed again. */
  window.addEventListener("pageshow", function (e) {
    if (!e.persisted) return;
    [].forEach.call(document.querySelectorAll(".cart-upsell__add[aria-disabled='true']"), reset);
  });
})();
